<template>
  <div class="students-section" v-loading="studentsLoading" element-loading-text="加载中..." element-loading-spinner="el-icon-loading" element-loading-background="rgba(0, 0, 0, 1)">
    <div class="students-grid" :class="{ 'loading': studentsLoading }">
      <div 
        v-for="student in sortedStudents"
        :key="student.id"
        style="position: relative;">
        <el-button
          :class="getStudentButtonClass(student)"
          @click="toggleStudentStatus(student)"
          @touchstart.native="recordPressStart(student, $event)"
          @touchend.native="recordPressEnd($event)"
          @touchmove.native="handleTouchMove($event)"
          class="student-btn"
          style="text-align: center; overflow: hidden; text-overflow: ellipsis; box-sizing: border-box;">
          <span style="text-align: center; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; display: block;">{{ student.studentNumber }}.{{ student.name }}</span>
        </el-button>
        <div v-if="showUnfinishedCount && showUnfinishedCountBadge && studentUnfinishedCounts[student.id] && studentUnfinishedCounts[student.id] > 0" 
             class="mobile-unfinished-badge">
          {{ studentUnfinishedCounts[student.id] > 99 ? '99+' : studentUnfinishedCounts[student.id] }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import EventBus from '../../utils/EventBus'

export default {
  name: 'StudentGridMobile',
  props: {
    students: {
      type: Array,
      default: () => []
    },
    correctionRecords: {
      type: Array,
      default: () => []
    },
    currentDate: {
      type: String,
      default: ''
    },
    selectedType: {
      type: Number,
      default: 1
    },
    classId: {
      type: Number,
      default: null
    },
    subjectIndex: {
      type: Number,
      default: 0
    },
    showUnfinishedCount: {
      type: Boolean,
      default: false
    },
    studentUnfinishedCounts: {
      type: Object,
      default: () => ({})
    },
    isAssigned: {
      type: Boolean,
      default: true
    },
    studentsLoading: {
      type: Boolean,
      default: false
    },
    showUnfinishedCountBadge: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      longPressTimer: null,
      isLongPressTriggered: false,
      selectedStudent: null,
      touchStartPosition: { x: 0, y: 0 },
      touchMoveThreshold: 10
    };
  },
  computed: {
    sortedStudents() {
      return [...this.students].sort((a, b) => {
        return a.studentNumber.localeCompare(b.studentNumber, undefined, {
          numeric: true,
          sensitivity: 'base'
        });
      });
    }
  },
  mounted() {
    this.recordPressEndBound = this.recordPressEnd.bind(this);
    document.addEventListener('mouseup', this.recordPressEndBound);
  },
  beforeDestroy() {
    document.removeEventListener('mouseup', this.recordPressEndBound);
  },
  methods: {
    getStudentButtonClass(student) {
      if (!this.isAssigned) {
        return 'unassigned';
      }
      const record = this.correctionRecords.find(r => r.studentId === student.id);
      if (record && record.corrected) {
        return 'finished';
      }
      return 'unfinished';
    },
    
    toggleStudentStatus(student) {
      if (this.isLongPressTriggered) {
        this.isLongPressTriggered = false;
        return;
      }
      
      // 如果未布置作业，提示用户
      if (!this.isAssigned) {
        this.$message.warning('请先点击布置作业按钮！');
        EventBus.$emit('trigger-assign-homework-flash');
        return;
      }
      
      let record = this.correctionRecords.find(r => r.studentId === student.id);

      if (!record) {
        record = {
          date: this.currentDate,
          studentId: student.id,
          homeworkTypeId: this.selectedType,
          subjectId: this.getSubjectIdByHomeworkType(this.selectedType),
          corrected: true
        };

        if (this.classId) {
          record.classId = this.classId;
        }

        this.$http.post('/correctionRecords', record)
          .then(response => {
            this.$emit('record-updated', response.data);
            this.$emit('update-stats');
            if (this.showUnfinishedCount) {
              this.$emit('load-unfinished-counts');
            }
          });
      } else {
        record.corrected = !record.corrected;
        if (this.classId && !record.classId) {
          record.classId = this.classId;
        }
        this.$http.post('/correctionRecords', record)
          .then(() => {
            this.$emit('update-stats');
            if (this.showUnfinishedCount) {
              this.$emit('load-unfinished-counts');
            }
          });
      }
    },
    
    getSubjectIdByHomeworkType(homeworkTypeId) {
      return this.subjectIndex + 1;
    },
    
    checkStudentHistory() {
      if (this.selectedStudent) {
        this.$emit('check-student-history', this.selectedStudent);
      }
    },
    
    recordPressStart(student, event) {
      this.isLongPressTriggered = false;
      this.selectedStudent = student;
      this.longPressTimer = setTimeout(() => {
        this.isLongPressTriggered = true;
        // 直接触发彻查作业，不显示弹出菜单
        this.checkStudentHistory();
      }, 500);
      
      if (event.touches) {
        this.touchStartPosition.x = event.touches[0].clientX;
        this.touchStartPosition.y = event.touches[0].clientY;
      }
    },
    
    recordPressEnd() {
      clearTimeout(this.longPressTimer);
    },
    
    handleTouchMove(event) {
      if (event.touches) {
        const touchX = event.touches[0].clientX;
        const touchY = event.touches[0].clientY;
        const deltaX = Math.abs(touchX - this.touchStartPosition.x);
        const deltaY = Math.abs(touchY - this.touchStartPosition.y);
        
        if (deltaX > this.touchMoveThreshold || deltaY > this.touchMoveThreshold) {
          clearTimeout(this.longPressTimer);
        }
      }
    }
  }
};
</script>

<style scoped>
.students-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  padding: 0 3px;
  box-sizing: border-box;
  width: 100%;
  margin: 0;
}

.student-btn {
  width: 100%;
  height: 40px;
  padding: 0;
  font-size: 12px;
  border-radius: 8px;
  border: none;
  transition: all 0.3s ease;
}

.students-grid.loading .student-btn,
.students-grid.loading .el-button,
.students-grid.loading .el-button span {
  transition: none !important;
}

.student-btn.unfinished {
  background-color: #f56c6c;
  color: white;
}

.student-btn.finished {
  background-color: #67c23a;
  color: white;
}

.student-btn.unassigned {
  background-color: #909399;
  color: white;
}

.students-section .el-button.unassigned {
  width: 100% !important;
  padding: 0 !important;
  box-sizing: border-box !important;
}

.mobile-unfinished-badge {
  position: absolute;
  top: -5px;
  right: -5px;
  background-color: #FFA500;
  color: white;
  border-radius: 50%;
  min-width: 20px;
  height: 20px;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  padding: 0 2px;
  z-index: 999;
}
</style>
